import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { getTickets, createTicket, updateTicket, getBots, getClients } from '@/lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Ticket,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  User,
  Phone,
  FileText
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const STATUS_CONFIG = {
  pending: { label: 'Pendiente', icon: Clock, class: 'status-pending' },
  in_progress: { label: 'En Proceso', icon: AlertCircle, class: 'status-paused' },
  completed: { label: 'Completado', icon: CheckCircle, class: 'status-completed' },
  cancelled: { label: 'Cancelado', icon: XCircle, class: 'bg-red-500/20 text-red-400 border border-red-500/30' }
};

const TicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [bots, setBots] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBot, setFilterBot] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [formData, setFormData] = useState({
    bot_id: '',
    client_id: '',
    customer_name: '',
    customer_phone: '',
    service: '',
    description: '',
    quantity: 1,
    payment_method: '',
    estimated_amount: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ticketsRes, botsRes, clientsRes] = await Promise.all([
        getTickets(),
        getBots(),
        getClients()
      ]);
      setTickets(ticketsRes.data);
      setBots(botsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Error al cargar los tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.bot_id || !formData.customer_name || !formData.service || !formData.description) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    // Get client_id from bot
    const bot = bots.find(b => b.id === formData.bot_id);
    const ticketData = {
      ...formData,
      client_id: bot?.client_id || '',
      estimated_amount: formData.estimated_amount ? parseFloat(formData.estimated_amount) : null
    };

    try {
      await createTicket(ticketData);
      toast.success('Ticket creado exitosamente');
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Error al crear el ticket');
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      await updateTicket(ticketId, { status: newStatus });
      toast.success('Estado actualizado');
      fetchData();
      
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Error al actualizar el estado');
    }
  };

  const resetForm = () => {
    setFormData({
      bot_id: '',
      client_id: '',
      customer_name: '',
      customer_phone: '',
      service: '',
      description: '',
      quantity: 1,
      payment_method: '',
      estimated_amount: ''
    });
  };

  const getBotName = (botId) => {
    const bot = bots.find(b => b.id === botId);
    return bot?.name || 'Bot desconocido';
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = 
      ticket.ticket_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.customer_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesBot = filterBot === 'all' || ticket.bot_id === filterBot;
    return matchesSearch && matchesStatus && matchesBot;
  });

  const renderTicketForm = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="bot_id">Bot *</Label>
        <Select
          value={formData.bot_id}
          onValueChange={(value) => setFormData({ ...formData, bot_id: value })}
        >
          <SelectTrigger data-testid="ticket-bot-select">
            <SelectValue placeholder="Selecciona un bot" />
          </SelectTrigger>
          <SelectContent>
            {bots.map((bot) => (
              <SelectItem key={bot.id} value={bot.id}>
                {bot.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customer_name">Nombre del Cliente *</Label>
          <Input
            id="customer_name"
            data-testid="ticket-customer-name-input"
            value={formData.customer_name}
            onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
            placeholder="Nombre completo"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="customer_phone">Teléfono</Label>
          <Input
            id="customer_phone"
            data-testid="ticket-customer-phone-input"
            value={formData.customer_phone}
            onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
            placeholder="+52 55 1234 5678"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="service">Servicio *</Label>
        <Input
          id="service"
          data-testid="ticket-service-input"
          value={formData.service}
          onChange={(e) => setFormData({ ...formData, service: e.target.value })}
          placeholder="Ej: Cambio de aceite, Consulta médica..."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Descripción *</Label>
        <Textarea
          id="description"
          data-testid="ticket-description-input"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Detalles del servicio o producto..."
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="quantity">Cantidad</Label>
          <Input
            id="quantity"
            type="number"
            min="1"
            data-testid="ticket-quantity-input"
            value={formData.quantity}
            onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="estimated_amount">Monto Estimado</Label>
          <Input
            id="estimated_amount"
            type="number"
            step="0.01"
            data-testid="ticket-amount-input"
            value={formData.estimated_amount}
            onChange={(e) => setFormData({ ...formData, estimated_amount: e.target.value })}
            placeholder="0.00"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="payment_method">Método de Pago</Label>
        <Select
          value={formData.payment_method}
          onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecciona un método" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="efectivo">Efectivo</SelectItem>
            <SelectItem value="tarjeta">Tarjeta</SelectItem>
            <SelectItem value="transferencia">Transferencia</SelectItem>
            <SelectItem value="otro">Otro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
          Cancelar
        </Button>
        <Button data-testid="create-ticket-btn" onClick={handleCreate} className="btn-glow">
          Crear Ticket
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 w-48 bg-muted animate-pulse rounded" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tickets-page">
      {/* Header V4.3 - ADN Fluxo Co */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 pt-4">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <div className="flex items-center gap-2 mb-3">
            <Ticket className="w-5 h-5 text-blue-500" />
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-blue-500/80">Soporte Operativo</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit">
            Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Tickets</span>
          </h1>
          <p className="text-slate-500 mt-2 font-medium italic text-sm">Gestión omnicanal de incidencias, solicitudes y resoluciones en tiempo real</p>
        </motion.div>

        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button 
              data-testid="add-ticket-btn" 
              className="group relative overflow-hidden h-12 px-6 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-500/20 transition-all duration-300 hover:shadow-blue-500/30 hover:scale-[1.02]" 
              disabled={bots.length === 0}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/10 to-blue-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              <Plus className="w-5 h-5 mr-2 relative z-10" />
              <span className="relative z-10">Nuevo Ticket</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg bg-[#0F172A] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-outfit text-white text-xl">Crear Nuevo Ticket</DialogTitle>
            </DialogHeader>
            {renderTicketForm()}
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
          <Input
            data-testid="ticket-search-input"
            placeholder="Buscar por número o cliente..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-950/50 border-white/5 rounded-xl h-11 text-white placeholder:text-slate-600 focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all duration-300"
          />
        </div>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40" data-testid="filter-status-select">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendientes</SelectItem>
            <SelectItem value="in_progress">En Proceso</SelectItem>
            <SelectItem value="completed">Completados</SelectItem>
            <SelectItem value="cancelled">Cancelados</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterBot} onValueChange={setFilterBot}>
          <SelectTrigger className="w-48" data-testid="filter-bot-select">
            <SelectValue placeholder="Bot" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los bots</SelectItem>
            {bots.map((bot) => (
              <SelectItem key={bot.id} value={bot.id}>
                {bot.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(STATUS_CONFIG).map(([status, config]) => {
          const count = tickets.filter(t => t.status === status).length;
          const Icon = config.icon;
          return (
            <Card key={status} className="card-hover">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.class}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-outfit">{count}</p>
                  <p className="text-xs text-muted-foreground">{config.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tickets list */}
      {filteredTickets.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Ticket className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="empty-state-title">No hay tickets</h3>
          <p className="empty-state-description">
            {searchQuery || filterStatus !== 'all' || filterBot !== 'all'
              ? 'No se encontraron tickets con los filtros aplicados'
              : 'Los tickets aparecerán aquí cuando se creen desde las conversaciones o manualmente'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map((ticket, index) => {
            const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card 
                key={ticket.id}
                className={`cursor-pointer card-hover animate-fade-in stagger-${(index % 5) + 1}`}
                onClick={() => {
                  setSelectedTicket(ticket);
                  setIsDetailOpen(true);
                }}
                data-testid={`ticket-card-${ticket.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${statusConfig.class}`}>
                        <StatusIcon className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium font-mono">{ticket.ticket_number}</p>
                          <Badge variant="outline" className="text-xs">
                            {getBotName(ticket.bot_id)}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {ticket.customer_name} • {ticket.service}
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded-full text-xs ${statusConfig.class}`}>
                        {statusConfig.label}
                      </span>
                      {ticket.estimated_amount && (
                        <p className="text-sm font-medium mt-2">
                          ${ticket.estimated_amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(new Date(ticket.created_at), 'dd MMM, HH:mm', { locale: es })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Ticket detail sheet */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="font-outfit flex items-center gap-2">
              <Ticket className="w-5 h-5" />
              {selectedTicket?.ticket_number}
            </SheetTitle>
          </SheetHeader>
          
          {selectedTicket && (
            <div className="mt-6 space-y-6">
              {/* Status */}
              <div>
                <Label className="text-muted-foreground text-xs">Estado</Label>
                <Select
                  value={selectedTicket.status}
                  onValueChange={(value) => handleUpdateStatus(selectedTicket.id, value)}
                >
                  <SelectTrigger className="mt-1" data-testid="ticket-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                      <SelectItem key={status} value={status}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Customer info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Cliente
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="font-medium">{selectedTicket.customer_name}</p>
                  {selectedTicket.customer_phone && (
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {selectedTicket.customer_phone}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Service info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Servicio
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="font-medium">{selectedTicket.service}</p>
                  <p className="text-sm text-muted-foreground">{selectedTicket.description}</p>
                  <div className="flex items-center gap-4 pt-2 text-sm">
                    <span>Cantidad: {selectedTicket.quantity || 1}</span>
                    {selectedTicket.payment_method && (
                      <span>Pago: {selectedTicket.payment_method}</span>
                    )}
                  </div>
                  {selectedTicket.estimated_amount && (
                    <p className="text-lg font-bold pt-2">
                      ${selectedTicket.estimated_amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Timeline */}
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Creado: {format(new Date(selectedTicket.created_at), "dd MMMM yyyy, HH:mm", { locale: es })}</p>
                <p>Actualizado: {format(new Date(selectedTicket.updated_at), "dd MMMM yyyy, HH:mm", { locale: es })}</p>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default TicketsPage;
